import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";

const ConfirmCtx = createContext(null);

/**
 * Promise-based in-app confirmation dialog.
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, confirmLabel, danger });
 *   if (!ok) return;
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, confirmLabel, cancelLabel, danger }
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts?.title || "Are you sure?",
        message: opts?.message || "",
        confirmLabel: opts?.confirmLabel || "Confirm",
        cancelLabel: opts?.cancelLabel || "Cancel",
        danger: !!opts?.danger,
      });
    });
  }, []);

  const close = useCallback((value) => {
    setState(null);
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  }, []);

  // ESC closes (cancels), Enter confirms
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(false); }
      else if (e.key === "Enter") { e.preventDefault(); close(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="modal-overlay"
          onClick={() => close(false)}
          data-testid="confirm-dialog-overlay"
          style={{ zIndex: 1100 }}
        >
          <div
            className="modal-card confirm-card"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-dialog"
            role="dialog"
            aria-modal="true"
          >
            <h3 style={{ margin: 0, fontSize: 18 }} data-testid="confirm-title">{state.title}</h3>
            {state.message && (
              <p style={{ marginTop: 10, marginBottom: 0, color: "var(--ink-2)", fontSize: 14, lineHeight: 1.55 }} data-testid="confirm-message">
                {state.message}
              </p>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 22, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => close(false)}
                data-testid="confirm-cancel"
                autoFocus={!state.danger}
              >
                {state.cancelLabel}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${state.danger ? "btn-danger" : "btn-clay"}`}
                onClick={() => close(true)}
                data-testid="confirm-ok"
                autoFocus={state.danger}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Safe fallback so a stray import outside the provider doesn't crash the page
    return async (opts) => window.confirm(opts?.message || opts?.title || "Are you sure?");
  }
  return ctx;
}
